import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  Avatar,
  AvatarGroup,
  alpha,
} from '@mui/material';
import {
  Search,
  Plus,
  Filter,
  Play,
  MoreVertical,
  Clock,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Link2,
  GitMerge,
  ArrowUpRight,
  Lock,
  Layers,
  Calendar,
} from 'lucide-react';
import dayjs from 'dayjs';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, startTimerAsync } from '@/store/slices/timerSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { Task, TaskStatus, TaskPriority, TaskCategory, IssueType } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import StatusBadge from '@/shared/components/StatusBadge';

// Category colors for display
const categoryColors: Record<TaskCategory, string> = {
  development: '#2563eb',
  design: '#8b5cf6',
  testing: '#16a34a',
  documentation: '#0891b2',
  research: '#7c3aed',
  meeting: '#f59e0b',
  review: '#ec4899',
  support: '#14b8a6',
  maintenance: '#6366f1',
  deployment: '#ef4444',
  planning: '#64748b',
  other: '#9ca3af',
};

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#2563eb', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

interface ProjectTasksTabProps {
  projectId: string;
  tasks: Task[];
  isLoading: boolean;
  onCreateTask: () => void;
  onTaskClick?: (taskId: string) => void;
}

/**
 * ProjectTasksTab
 * Task list specific to a project with filtering
 * Shows category, parent task link, issue type, and dependency indicators
 */
const ProjectTasksTab: React.FC<ProjectTasksTabProps> = ({
  projectId,
  tasks,
  isLoading,
  onCreateTask,
  onTaskClick,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'all'>('all');

  // Get parent task lookup for displaying parent links
  const taskLookup = useMemo(() => {
    const lookup = new Map<string, Task>();
    tasks.forEach((task) => lookup.set(task.id, task));
    return lookup;
  }, [tasks]);

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      !searchQuery ||
      task.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority =
      priorityFilter === 'all' || task.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleTaskClick = (taskId: string) => {
    if (onTaskClick) {
      onTaskClick(taskId);
    } else {
      navigate(`/tasks?taskId=${taskId}`);
    }
  };

  const handleStartTimer = (e: React.MouseEvent, taskId: string, taskTitle: string) => {
    e.stopPropagation();
    if (user) {
      dispatch(startTimerAsync({ taskId, userId: user.id, taskTitle }));
    }
  };

  /** Check if current user can use timer (ONLY Responsible users) */
  const canUseTimer = (task: Task): boolean => {
    const userId = user?.id;
    if (!userId) return false;
    return task.raci?.responsible?.includes(userId) || false;
  };

  const getDeadlineStyle = (dueDate: string, status: TaskStatus) => {
    if (status === TaskStatus.COMPLETED) {
      return { color: 'text.secondary' };
    }
    const now = dayjs();
    const deadline = dayjs(dueDate);
    const hoursUntil = deadline.diff(now, 'hour');

    if (hoursUntil < 0) return { color: 'error.main', fontWeight: 600 };
    if (hoursUntil < 24) return { color: 'warning.main', fontWeight: 500 };
    return { color: 'text.secondary' };
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TaskStatus | 'all')}
              displayEmpty
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value={TaskStatus.NOT_STARTED}>Not Started</MenuItem>
              <MenuItem value={TaskStatus.IN_PROGRESS}>In Progress</MenuItem>
              <MenuItem value={TaskStatus.BLOCKED}>Blocked</MenuItem>
              <MenuItem value={TaskStatus.COMPLETED}>Completed</MenuItem>
            </Select>
          </FormControl>

          {/* Priority Filter */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={priorityFilter}
              onChange={(e) =>
                setPriorityFilter(e.target.value as TaskPriority | 'all')
              }
              displayEmpty
            >
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value={TaskPriority.P4}>Low (P4)</MenuItem>
              <MenuItem value={TaskPriority.P3}>Medium (P3)</MenuItem>
              <MenuItem value={TaskPriority.P2}>High (P2)</MenuItem>
              <MenuItem value={TaskPriority.P1}>Critical (P1)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={onCreateTask}
        >
          Add Task
        </Button>
      </Box>

      {/* Tasks Table */}
      <Card variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Task</TableCell>
                <TableCell width={100}>Category</TableCell>
                <TableCell width={80}>Type</TableCell>
                <TableCell width={80}>Priority</TableCell>
                <TableCell width={120}>Status</TableCell>
                <TableCell width={80}>Links</TableCell>
                <TableCell width={100}>Start Date</TableCell>
                <TableCell width={140}>Due Date</TableCell>
                <TableCell width={120}>Assignee</TableCell>
                <TableCell width={100} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      {tasks.length === 0
                        ? 'No tasks in this project yet'
                        : 'No tasks match your filters'}
                    </Typography>
                    {tasks.length === 0 && (
                      <Button
                        startIcon={<Plus size={16} />}
                        onClick={onCreateTask}
                        sx={{ mt: 1 }}
                      >
                        Create First Task
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => {
                  const isTimerActive = activeTimers.some(t => t.taskId === task.id);
                  const deadlineStyle = getDeadlineStyle(task.dueDate, task.status);
                  const parentTask = task.parentTaskId ? taskLookup.get(task.parentTaskId) : null;
                  const subtaskCount = task.subtaskIds?.length || 0;
                  const hasBlockers = (task.blockedByTaskIds?.length || 0) > 0;
                  const isBlocking = (task.blockingTaskIds?.length || 0) > 0;
                  const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

                  return (
                    <TableRow
                      key={task.id}
                      hover
                      onClick={() => handleTaskClick(task.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isTimerActive 
                          ? 'success.50' 
                          : hasBlockers 
                          ? alpha('#f59e0b', 0.05) 
                          : 'inherit',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={task.status === TaskStatus.COMPLETED}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              textDecoration:
                                task.status === TaskStatus.COMPLETED
                                  ? 'line-through'
                                  : 'none',
                            }}
                          >
                            {task.title}
                          </Typography>
                          {/* Parent Task Link */}
                          {parentTask && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskClick(parentTask.id);
                              }}
                            >
                              <ArrowUpRight size={12} style={{ color: '#6366f1' }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'primary.main',
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' },
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Subtask of: {parentTask.title}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      {/* Category Column */}
                      <TableCell>
                        {task.category ? (
                          <Chip
                            label={task.category.charAt(0).toUpperCase() + task.category.slice(1)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              bgcolor: alpha(categoryColors[task.category] || '#9ca3af', 0.15),
                              color: categoryColors[task.category] || '#9ca3af',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      {/* Issue Type Column */}
                      <TableCell>
                        {task.issueType && IssueIcon ? (
                          <Tooltip title={issueTypeConfig[task.issueType].label}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <IssueIcon
                                size={14}
                                style={{ color: issueTypeConfig[task.issueType].color }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: issueTypeConfig[task.issueType].color,
                                  fontWeight: 500,
                                }}
                              >
                                {issueTypeConfig[task.issueType].label}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <PriorityBadge priority={task.priority} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={task.status} />
                      </TableCell>
                      {/* Links/Dependencies Column */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Subtasks */}
                          {subtaskCount > 0 && (
                            <Tooltip title={`${subtaskCount} subtask(s)`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Layers size={12} style={{ color: '#8b5cf6' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {subtaskCount}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {/* Blocked by */}
                          {hasBlockers && (
                            <Tooltip title={`Blocked by ${task.blockedByTaskIds?.length} task(s)`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Lock size={12} style={{ color: '#f59e0b' }} />
                                <Typography variant="caption" sx={{ color: '#f59e0b' }}>
                                  {task.blockedByTaskIds?.length}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {/* Blocking others */}
                          {isBlocking && (
                            <Tooltip title={`Blocking ${task.blockingTaskIds?.length} task(s)`}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <GitMerge size={12} style={{ color: '#6366f1' }} />
                                <Typography variant="caption" sx={{ color: '#6366f1' }}>
                                  {task.blockingTaskIds?.length}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {/* Dependencies link */}
                          {(hasBlockers || isBlocking) && (
                            <Tooltip title="Has dependencies">
                              <Link2 size={12} style={{ color: '#64748b' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      {/* Start Date Column */}
                      <TableCell>
                        {task.startDate ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Calendar size={14} style={{ color: '#94a3b8' }} />
                            <Typography variant="body2" color="text.secondary">
                              {dayjs(task.startDate).format('MMM D')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Clock size={14} style={{ color: '#94a3b8' }} />
                          <Typography variant="body2" sx={deadlineStyle}>
                            {dayjs(task.dueDate).format('MMM D, h:mm A')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <AvatarGroup max={2} sx={{ justifyContent: 'flex-start' }}>
                          {task.raci.responsible.map((userId, idx) => {
                            const displayName = task.raciNames?.responsible?.[idx] || userId;
                            return (
                              <Tooltip key={userId} title={displayName}>
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: '0.75rem',
                                    bgcolor: 'primary.main',
                                  }}
                                >
                                  {displayName.charAt(0).toUpperCase()}
                                </Avatar>
                              </Tooltip>
                            );
                          })}
                        </AvatarGroup>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          {task.status !== TaskStatus.COMPLETED && canUseTimer(task) && (
                            <Tooltip title={isTimerActive ? 'Timer active' : 'Start timer'}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleStartTimer(e, task.id, task.title)}
                                disabled={isTimerActive}
                              >
                                <Play
                                  size={14}
                                  fill={isTimerActive ? '#16a34a' : '#2563eb'}
                                  style={{ color: isTimerActive ? '#16a34a' : '#2563eb' }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical size={16} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${filteredTasks.length} tasks`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={`${filteredTasks.filter((t) => t.status === TaskStatus.COMPLETED).length} completed`}
          size="small"
          sx={{ bgcolor: 'success.50', color: 'success.main' }}
        />
        <Chip
          label={`${filteredTasks.filter((t) => dayjs(t.dueDate).isBefore(dayjs()) && t.status !== TaskStatus.COMPLETED).length} overdue`}
          size="small"
          sx={{ bgcolor: 'error.50', color: 'error.main' }}
        />
        <Chip
          icon={<Layers size={12} />}
          label={`${filteredTasks.filter((t) => t.parentTaskId).length} subtasks`}
          size="small"
          sx={{ bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }}
        />
        <Chip
          icon={<Lock size={12} />}
          label={`${filteredTasks.filter((t) => (t.blockedByTaskIds?.length || 0) > 0).length} blocked`}
          size="small"
          sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}
        />
      </Box>
    </Box>
  );
};

export default ProjectTasksTab;
